/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  ListItemIcon as MuiListItemIcon,
  ListItemText,
  MenuItem as MUIMenuItem,
  styled,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import MaterialSymbol from "../../../components/MaterialSymbol";
import Typography from "../../../components/Typography";
import { usePermission } from "../../../hooks/usePermission";
import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import TestIds from "../../../testIds";
import { ScrollYPositionKeys } from "../../../util/consts";
import { saveScrollYPosition } from "../../../util/helpers";
import { QUOTES_ROUTES } from "../../quotes/constants";

const QuickLinkWrapper = styled("div")(({ theme }) => ({
  display: "none",
  flexDirection: "column",
  gap: "8px",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    display: "flex",
  },
}));

const ButtonsWrapper = styled("div")(() => ({
  display: "flex",
  gap: "8px",
  width: "100%",
  flexDirection: "row",
}));

const Button = styled(MUIMenuItem)(({ theme }) => ({
  backgroundColor: "white",
  padding: "12px 8px",
  border: `1px ${theme.colors.primary100}`,
  // minWidth: '50%',
  width: "100%",
}));

interface LinkButtonProps {
  onClick: () => void;
  iconId: string;
  label: string;
  testId: string;
}

const LinkButton: React.FC<LinkButtonProps> = ({ onClick, iconId, label, testId }) => {
  return (
    <Button farmlands-variant="light" onClick={onClick} data-testid={testId}>
      <MuiListItemIcon>
        <MaterialSymbol>{iconId}</MaterialSymbol>
      </MuiListItemIcon>
      <ListItemText>{label}</ListItemText>
    </Button>
  );
};

export const QuickLinks = () => {
  const navigate = useNavigate();
  const remoteConfig = useSelector(selectRemoteConfig);
  const canViewSalesAgreements = usePermission("SalesAgreements_AgreementView").allowed;
  const canViewQuotes = usePermission("Quotes_QuoteView").allowed;

  if (!canViewSalesAgreements && !canViewQuotes) {
    return null;
  }

  return (
    <QuickLinkWrapper data-testid={TestIds.components.quicklink.component}>
      <Typography variant="h4">Quick links</Typography>
      <ButtonsWrapper>
        {canViewSalesAgreements && (
          <LinkButton
            label="Agreements"
            iconId="Note"
            testId={TestIds.components.quicklink.agreements}
            onClick={() => {
              saveScrollYPosition({
                sessionStorageKey: ScrollYPositionKeys.Agreements,
                position: 0,
              });
              navigate("/agreements");
            }}
          />
        )}
        {remoteConfig.quoteEnabled && canViewQuotes && (
          <LinkButton
            label="Quotes"
            iconId="request_quote"
            testId={TestIds.components.quicklink.quotes}
            onClick={() => {
              saveScrollYPosition({
                sessionStorageKey: ScrollYPositionKeys.Quotes,
                position: 0,
              });
              navigate(QUOTES_ROUTES.BASE);
            }}
          />
        )}
      </ButtonsWrapper>
    </QuickLinkWrapper>
  );
};
